class PdpBundlePicker extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._checkoutInProgress = false;

    this._cards = Array.from(
      this.querySelectorAll('.pdp-bundle-picker__card-wrap')
    );

    this._mobileCta = this.querySelector(
      '.pdp-bundle-picker__mobile-cta'
    );

    this._mobileCtaLabel = this.querySelector(
      '.pdp-bundle-picker__mobile-cta-label'
    );

    this._mobileCtaPrice = this.querySelector(
      '.pdp-bundle-picker__mobile-cta-price'
    );

    this._mobileCtaCompareSel = this.querySelector(
      '.pdp-bundle-picker__mobile-cta-compare s'
    );

    if (!this._cards.length) return;

    /*
    |--------------------------------------------------------------------------
    | DESKTOP CARDS
    |--------------------------------------------------------------------------
    */

    this._cards.forEach((card) => {
      const cardCta = card.querySelector(
        '.pdp-bundle-picker__card-cta'
      );

      /*
       * IMPORTANT:
       * Remove actual cart permalink from anchor.
       *
       * data-checkout-url on card is still used by JS.
       *
       * This prevents another theme/app click handler
       * from navigating to the normal cart permalink.
       */
      if (cardCta) {
        cardCta.setAttribute('href', '#');
        cardCta.setAttribute('role', 'button');
      }

      card.addEventListener('click', (event) => {
        if (
          event.target.closest('.pdp-bundle-picker__card-cta') ||
          event.target.closest('.pdp-bundle-picker__card-cta_new')
        ) {
          return;
        }

        this._selectCard(card);
      });

      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this._selectCard(card);
        }
      });

      /*
       * Subscription CTA
       */
      if (cardCta) {
        cardCta.addEventListener(
          'click',
          async (event) => {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            if (this._checkoutInProgress) return;

            this._selectCard(card);

            await this._handleSubscriptionCheckout(
              card,
              cardCta
            );
          },
          true
        );
      }

      /*
       * Custom CTA:
       * Leave its own custom URL working normally.
       */
      const customCta = card.querySelector(
        '.pdp-bundle-picker__card-cta_new'
      );

      if (customCta) {
        customCta.addEventListener(
          'click',
          (event) => {
            event.stopPropagation();
          },
          true
        );
      }
    });

    /*
    |--------------------------------------------------------------------------
    | MOBILE CTA
    |--------------------------------------------------------------------------
    */

    if (this._mobileCta) {
      /*
       * Do not allow the mobile anchor itself
       * to contain/run the cart permalink.
       */
      this._mobileCta.setAttribute('href', '#');
      this._mobileCta.setAttribute('role', 'button');

      this._mobileCta.addEventListener(
        'click',
        async (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          if (this._checkoutInProgress) return;

          const selectedCard =
            this.querySelector(
              '.pdp-bundle-picker__card-wrap.is-selected'
            ) ||
            this._cards.find((card) =>
              card.hasAttribute('data-featured')
            ) ||
            this._cards[0];

          if (!selectedCard) return;

          await this._handleSubscriptionCheckout(
            selectedCard,
            this._mobileCta
          );
        },
        true
      );
    }

    /*
    |--------------------------------------------------------------------------
    | DEFAULT CARD
    |--------------------------------------------------------------------------
    */

    const defaultCard =
      this._cards.find((card) =>
        card.hasAttribute('data-featured')
      ) ||
      this._cards[0];

    this._selectCard(defaultCard);
  }


  /*
  |--------------------------------------------------------------------------
  | SHOPIFY ROOT
  |--------------------------------------------------------------------------
  */

  _getShopifyRoot() {
    if (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) {
      return window.Shopify.routes.root;
    }

    return '/';
  }


  /*
  |--------------------------------------------------------------------------
  | SELECT CARD
  |--------------------------------------------------------------------------
  */

  _selectCard(selected) {
    if (!selected) return;

    this._cards.forEach((card) => {
      card.classList.remove('is-selected');
      card.setAttribute('aria-checked', 'false');
    });

    selected.classList.add('is-selected');
    selected.setAttribute('aria-checked', 'true');

    if (this._mobileCta) {
      this._updateMobileCta(selected);
    }
  }


  /*
  |--------------------------------------------------------------------------
  | MOBILE CTA
  |--------------------------------------------------------------------------
  */

  _updateMobileCta(card) {
    if (!card || !this._mobileCta) return;

    const salePrice =
      card.dataset.salePrice || '';

    const comparePrice =
      card.dataset.comparePrice || '';

    const ctaText =
      card.dataset.ctaText || 'Add to Cart';

    /*
     * IMPORTANT:
     * Never put checkout URL into actual mobile href.
     */
    this._mobileCta.setAttribute('href', '#');

    this._mobileCta.setAttribute(
      'aria-label',
      salePrice
        ? `${ctaText} — ${salePrice}`
        : ctaText
    );

    if (this._mobileCtaLabel) {
      this._mobileCtaLabel.textContent = ctaText;
    }

    if (this._mobileCtaPrice) {
      this._mobileCtaPrice.textContent = salePrice;
    }

    if (this._mobileCtaCompareSel) {
      this._mobileCtaCompareSel.textContent =
        comparePrice;
    }
  }


  /*
  |--------------------------------------------------------------------------
  | GET FIRST PRODUCT FROM CMS CART URL
  |--------------------------------------------------------------------------
  |
  | Example:
  |
  | /cart/49282911240347:1
  |
  | or
  |
  | /cart/49282911240347:1,48886447341723:1
  |
  | ONLY FIRST VARIANT IS USED.
  |
  */

  _getMainCartItemFromUrl(url) {
    if (!url || url === '#') return null;

    let parsedUrl;

    try {
      parsedUrl = new URL(
        url,
        window.location.origin
      );
    } catch (error) {
      console.error(
        '[pdp-bundle-picker] Invalid checkout URL:',
        url
      );

      return null;
    }

    const cartMatch =
      parsedUrl.pathname.match(/\/cart\/(.+)/);

    if (!cartMatch || !cartMatch[1]) {
      console.error(
        '[pdp-bundle-picker] URL must use /cart/VARIANT_ID:QTY format:',
        url
      );

      return null;
    }

    const firstItem = cartMatch[1]
      .split(',')[0]
      .trim();

    const parts = firstItem.split(':');

    const variantId =
      this._normalizeNumber(parts[0]);

    const quantity =
      this._normalizeNumber(parts[1]) || 1;

    if (!variantId) return null;

    return {
      id: variantId,
      quantity: quantity
    };
  }


  /*
  |--------------------------------------------------------------------------
  | NORMALIZE SHOPIFY ID
  |--------------------------------------------------------------------------
  */

  _normalizeNumber(value) {
    if (!value) return '';

    const match = String(value)
      .trim()
      .match(/(\d+)$/);

    return match
      ? Number(match[1])
      : '';
  }


  /*
  |--------------------------------------------------------------------------
  | GET SELLING PLAN
  |--------------------------------------------------------------------------
  */

  _getSellingPlanId(card) {
    if (!card) return '';

    let sellingPlanId =
      this._normalizeNumber(
        card.dataset.sellingPlanId
      );

    if (sellingPlanId) {
      return sellingPlanId;
    }

    const checkoutUrl =
      card.dataset.checkoutUrl || '';

    try {
      const parsedUrl = new URL(
        checkoutUrl,
        window.location.origin
      );

      sellingPlanId =
        this._normalizeNumber(
          parsedUrl.searchParams.get(
            'selling_plan'
          )
        ) ||
        this._normalizeNumber(
          parsedUrl.searchParams.get(
            'selling_plan_id'
          )
        );
    } catch (error) {
      sellingPlanId = '';
    }

    return sellingPlanId || '';
  }


  /*
  |--------------------------------------------------------------------------
  | SMALL DELAY
  |--------------------------------------------------------------------------
  */

  _sleep(ms) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }


  /*
  |--------------------------------------------------------------------------
  | GET CURRENT CART
  |--------------------------------------------------------------------------
  */

  async _getCart() {
    const root = this._getShopifyRoot();

    const response = await fetch(
      root + 'cart.js',
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        },
        cache: 'no-store'
      }
    );

    const data =
      await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data.description ||
        data.message ||
        'Could not read cart'
      );
    }

    return data;
  }


  /*
  |--------------------------------------------------------------------------
  | CLEAR CART
  |--------------------------------------------------------------------------
  */

  async _clearCart() {
    const root = this._getShopifyRoot();

    const response = await fetch(
      root + 'cart/clear.js',
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({})
      }
    );

    const data =
      await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data.description ||
        data.message ||
        'Could not clear cart'
      );
    }

    return data;
  }


  /*
  |--------------------------------------------------------------------------
  | ADD SUBSCRIPTION
  |--------------------------------------------------------------------------
  */

  async _addSubscriptionItem(cartItem) {
    const root = this._getShopifyRoot();

    const response = await fetch(
      root + 'cart/add.js',
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(cartItem.id),
              quantity: Number(
                cartItem.quantity || 1
              ),
              selling_plan: Number(
                cartItem.selling_plan
              )
            }
          ]
        })
      }
    );

    const data =
      await response.json().catch(() => ({}));

    if (!response.ok) {
      const error = new Error(
        data.description ||
        data.message ||
        data.status ||
        'Could not add subscription'
      );

      error.status = response.status;
      error.response = data;

      throw error;
    }

    return data;
  }


  /*
  |--------------------------------------------------------------------------
  | REMOVE ONE CART LINE
  |--------------------------------------------------------------------------
  */

  async _removeCartLine(lineKey) {
    if (!lineKey) return;

    const root = this._getShopifyRoot();

    const response = await fetch(
      root + 'cart/change.js',
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: String(lineKey),
          quantity: 0
        })
      }
    );

    const data =
      await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data.description ||
        data.message ||
        'Could not remove unwanted product'
      );
    }

    return data;
  }


  /*
  |--------------------------------------------------------------------------
  | GET SELLING PLAN FROM CART LINE
  |--------------------------------------------------------------------------
  */

  _getLineSellingPlanId(item) {
    if (
      !item ||
      !item.selling_plan_allocation ||
      !item.selling_plan_allocation.selling_plan
    ) {
      return '';
    }

    return this._normalizeNumber(
      item.selling_plan_allocation
        .selling_plan
        .id
    );
  }


  /*
  |--------------------------------------------------------------------------
  | CHECK EXPECTED SUBSCRIPTION LINE
  |--------------------------------------------------------------------------
  */

  _isExpectedSubscriptionLine(
    item,
    variantId,
    sellingPlanId
  ) {
    if (!item) return false;

    const lineVariantId =
      this._normalizeNumber(
        item.variant_id || item.id
      );

    const lineSellingPlanId =
      this._getLineSellingPlanId(item);

    return (
      Number(lineVariantId) ===
        Number(variantId) &&
      Number(lineSellingPlanId) ===
        Number(sellingPlanId)
    );
  }


  /*
  |--------------------------------------------------------------------------
  | REMOVE NORMAL / EXTRA ITEMS
  |--------------------------------------------------------------------------
  |
  | Only ONE subscription cart line is allowed.
  |
  */

  async _removeUnwantedCartItems(
    variantId,
    sellingPlanId
  ) {
    let cart = await this._getCart();

    const items = Array.isArray(cart.items)
      ? cart.items
      : [];

    let subscriptionFound = false;

    const unwantedItems = [];

    items.forEach((item) => {
      const isExpected =
        this._isExpectedSubscriptionLine(
          item,
          variantId,
          sellingPlanId
        );

      /*
       * Keep ONLY first correct subscription line.
       */
      if (isExpected && !subscriptionFound) {
        subscriptionFound = true;
        return;
      }

      /*
       * Everything else:
       *
       * - normal one-time item
       * - second product
       * - old cart product
       * - duplicate subscription
       *
       * REMOVE IT.
       */
      unwantedItems.push(item);
    });

    for (const item of unwantedItems) {
      if (!item.key) continue;

      console.warn(
        '[pdp-bundle-picker] Removing unwanted cart line:',
        {
          variantId: item.variant_id,
          key: item.key,
          sellingPlan:
            this._getLineSellingPlanId(item)
        }
      );

      await this._removeCartLine(item.key);
    }

    return await this._getCart();
  }


  /*
  |--------------------------------------------------------------------------
  | VERIFY FINAL CART
  |--------------------------------------------------------------------------
  */

  _validateFinalCart(
    cart,
    variantId,
    sellingPlanId,
    quantity
  ) {
    const items =
      cart && Array.isArray(cart.items)
        ? cart.items
        : [];

    /*
     * EXACTLY ONE CART LINE ONLY.
     */
    if (items.length !== 1) {
      return false;
    }

    const item = items[0];

    const correctSubscription =
      this._isExpectedSubscriptionLine(
        item,
        variantId,
        sellingPlanId
      );

    if (!correctSubscription) {
      return false;
    }

    if (
      Number(item.quantity) !==
      Number(quantity)
    ) {
      return false;
    }

    return true;
  }


  /*
  |--------------------------------------------------------------------------
  | MAIN SUBSCRIPTION CHECKOUT
  |--------------------------------------------------------------------------
  */

  _shouldOpenCartDrawerAfterAdd() {
    return this.dataset.openCartDrawerAfterAdd === 'true';
  }

  _openCartDrawerAfterAdd() {
    if (typeof window.updateCartDrawer === 'function') {
      window.updateCartDrawer();
      return;
    }

    const cartDrawer = document.querySelector('cart-drawer');
    document.dispatchEvent(new CustomEvent('cart:refresh'));

    if (!cartDrawer) return;

    if (typeof cartDrawer.show === 'function') {
      cartDrawer.show();
    } else if (typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else {
      cartDrawer.classList.add('active', 'animate');
      cartDrawer.setAttribute('open', '');
    }
  }

async _handleSubscriptionCheckout(card, button) {
  if (!card || !button) return;
  if (this._checkoutInProgress) return;

  const checkoutUrl = card.dataset.checkoutUrl || '';

  const selectedItem = this._getMainCartItemFromUrl(checkoutUrl);

  if (!selectedItem || !selectedItem.id) {
    console.error(
      '[pdp-bundle-picker] Variant missing:',
      checkoutUrl
    );

    alert(
      'Subscription product variant is missing. Please check the Checkout URL.'
    );

    return;
  }

  const sellingPlanId = this._getSellingPlanId(card);

  /*
   * Selling plan compulsory.
   * Never add normal one-time product.
   */
  if (!sellingPlanId) {
    console.error(
      '[pdp-bundle-picker] Selling plan ID missing.'
    );

    alert(
      'Selling plan ID is missing. Please add the subscription Selling Plan ID.'
    );

    return;
  }

  const cartItem = {
    id: Number(selectedItem.id),
    quantity: Number(selectedItem.quantity || 1),
    selling_plan: Number(sellingPlanId)
  };

  this._checkoutInProgress = true;
  this._setLoading(button, true);

  try {
    console.log(
      '[pdp-bundle-picker] Fast subscription checkout:',
      cartItem
    );

    /*
    |--------------------------------------------------------------------------
    | STEP 1
    | Remove ALL previous normal/subscription products.
    |--------------------------------------------------------------------------
    */

    await this._clearCart();


    /*
    |--------------------------------------------------------------------------
    | STEP 2
    | Add ONLY selected subscription product.
    |--------------------------------------------------------------------------
    */

    await this._addSubscriptionItem(cartItem);


    /*
    |--------------------------------------------------------------------------
    | STEP 3
    | IMMEDIATELY GO TO CHECKOUT
    |--------------------------------------------------------------------------
    |
    | No 400ms delay
    | No 250ms delay
    | No cart verification
    | No second clear
    | No retry
    | No cart drawer
    |
    */

    if (this._shouldOpenCartDrawerAfterAdd()) {
      this._checkoutInProgress = false;
      this._setLoading(button, false);
      this._openCartDrawerAfterAdd();
      return;
    }

    window.location.replace('/checkout');

  } catch (error) {
    console.error(
      '[pdp-bundle-picker] Subscription checkout error:',
      error
    );

    if (error.response) {
      console.error(
        '[pdp-bundle-picker] Shopify response:',
        error.response
      );
    }

    this._checkoutInProgress = false;

    this._setLoading(
      button,
      false
    );

    alert(
      error.message ||
      'Something went wrong. Please try again.'
    );
  }
}

  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  _setLoading(button, loading) {
    if (!button) return;

    button.classList.toggle(
      'is-loading',
      loading
    );

    button.setAttribute(
      'aria-busy',
      loading ? 'true' : 'false'
    );

    if (button.tagName === 'BUTTON') {
      button.disabled = loading;
    }
  }
}


if (
  !customElements.get(
    'pdp-bundle-picker'
  )
) {
  customElements.define(
    'pdp-bundle-picker',
    PdpBundlePicker
  );
}